/**
 * Budgets (roadmap F3): a spend limit per period on the organisation, a business area, a
 * project, a team or a person, computed from the usage workbenches and nodes report. Past the
 * warning line the people concerned are told; past the limit the budget's action applies:
 * warn, throttle (no new agent starts, running ones finish) or block (running agents stop at
 * their next turn). The override role may lift it for some hours, audited.
 *
 *   GET|POST /admin/v1/budgets, PATCH|DELETE /admin/v1/budgets/:id     administrators; GET carries each budget's standing
 *   GET  /projects/v1/:id/budget                                        members and their devices: the budgets that apply, spent, remaining, state
 *   POST /projects/v1/:id/budget/override { budgetId, reason, hours }   the override role: lift a throttled or blocked budget for a while, for this project only
 *   POST /admin/v1/budgets/:id/override { reason, hours }              administrators: lift it for every project (stored as project "")
 *   POST /admin/v1/budgets/evaluate                                     administrators: work every budget out now (it also runs on its own)
 *
 * What counts (cost round 1, AIOE 0.16.0): a money budget counts metered spend only, the calls a
 * provider bills per token. Calls on a paid seat and calls with no known price are not in it; a
 * token limit counts the tokens of every call.
 *
 * Cost round 2 (AIOE 0.18.0), every addition optional so an older workbench reads the answer as before:
 *
 * - **An evaluator** works each budget out after usage arrives (debounced per organisation) and every
 *   hour, stores where it stands, and tells people when it crosses 50, 80 or 100 per cent
 *   (`BUDGET_THRESHOLDS`), when the trailing seven days' run-rate says it will reach its limit before
 *   the period ends (`forecast.exceeds`), and when a project's state changes: inbox records, email,
 *   phones, and the relay order `budget.changed` (`budgetChangedOrderSchema`) to the online devices of
 *   the people in scope, so a workbench reacts in seconds rather than at its next poll.
 *   `GET /projects/v1/:id/budget` answers from that stored standing; it has no side effects.
 * - **A lift is per project.** `Budget.overrides` holds lifts keyed by project id; `Budget.override` stays
 *   the organisation-wide lift (project ""). A project's standing counts its own lift and the
 *   organisation-wide one, and `BudgetStanding.lift` says which applied.
 * - **Fail closed.** `ProjectBudgetResponse.failClosed` is the organisation's `budgets.failClosed`
 *   setting: true asks a workbench to stop new work when it cannot reach the platform (the default,
 *   false, keeps the last reading and carries on).
 * - **Periods in the organisation's time zone.** `ProjectBudgetResponse.timeZone` (IANA, "UTC" by
 *   default; set on `PUT /admin/v1/organisation` as `timeZone`, answered on `GET /people/v1/me/organisation`)
 *   is where days, weeks (Monday first) and months turn over (`budgetPeriod`).
 * - **Enforcement in dispatch.** Placement, dispatch and cloud-agent starts answer 409 `budget_blocked`
 *   or `budget_throttled` (`BUDGET_REFUSALS`) while a budget that applies holds work.
 *
 * Cost round 3 (AIOE, every addition optional):
 *
 * - **A hierarchy.** The organisation's budgets hold its business areas', and an area's hold its
 *   projects'. Team and person budgets stand beside the tree. A child cannot be set above a parent
 *   (`budgetExceeds`): `POST` and `PATCH /admin/v1/budgets` answer 400 `budget_exceeds_parent` (or
 *   `budget_below_child` when a parent is lowered under a child) with the budget named in words.
 * - **A default share.** An area budget with `childDefault: { percent }` gives every project in the
 *   area that has no budget of its own an inherited one: `percent` of the area's limit, same period
 *   and action. It is answered on `GET /projects/v1/:id/budget` as a standing with `inherited`
 *   (the area budget's id and the percent); its `budget.id` is `<area budget id>~<project id>`.
 *   A workbench treats it like any project budget (its state holds work the same way); lifting it
 *   (`POST /projects/v1/:id/budget/override` with that id) lifts the area budget for this project.
 *   `GET /admin/v1/budgets/inherited` lists them for the console's tree.
 *
 * Words for people: budget, spent, remaining, paused for budget, blocked for budget, lift; never quota.
 */
import { z } from "zod";
export const budgetScopeSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("tenant") }),
    z.object({ type: z.literal("area"), id: z.string().min(1).max(100) }),
    z.object({ type: z.literal("project"), id: z.string().min(1).max(200) }),
    z.object({ type: z.literal("team"), id: z.string().min(1).max(200) }),
    z.object({ type: z.literal("person"), id: z.string().min(1).max(200) }),
]);
export const budgetPeriodSchema = z.enum(["month", "week", "day"]);
export const budgetActionSchema = z.enum(["warn", "throttle", "block"]);
export const budgetOverrideRoleSchema = z.enum(["owner", "portfolio-manager", "admin"]);
export const budgetStateSchema = z.enum(["ok", "warning", "throttled", "blocked"]);
/** A lift in force: who, why, until when. */
export const budgetLiftSchema = z.object({ by: z.string(), reason: z.string(), at: z.string(), until: z.string() });
/** An area budget's default share for each of its projects without a budget of their own (cost round 3). */
export const budgetChildDefaultSchema = z.object({ percent: z.number().min(1).max(100) });
/** The lines every budget tells people about, in per cent of the limit reached first (cost round 2). */
export const BUDGET_THRESHOLDS = [50, 80, 100];
export const budgetSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(200),
    scope: budgetScopeSchema,
    period: budgetPeriodSchema,
    amountUsd: z.number().positive().max(100_000_000),
    /** A token limit beside the money, when set; whichever is reached first counts. */
    tokens: z.number().int().positive().optional(),
    warnAtPercent: z.number().int().min(1).max(100),
    action: budgetActionSchema,
    overrideRole: budgetOverrideRoleSchema,
    /** The organisation-wide lift (every project): who, why, until when. */
    override: budgetLiftSchema.optional(),
    /** Lifts for one project each, keyed by project id (cost round 2, optional). A project counts its own and the organisation-wide one. */
    overrides: z.record(z.string(), budgetLiftSchema).optional(),
    /** Area budgets only (cost round 3): the share each project without its own budget inherits. */
    childDefault: budgetChildDefaultSchema.optional(),
    createdBy: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const budgetCreateSchema = z.object({
    name: z.string().min(1).max(200),
    scope: budgetScopeSchema,
    period: budgetPeriodSchema.default("month"),
    amountUsd: z.number().positive().max(100_000_000),
    tokens: z.number().int().positive().optional(),
    warnAtPercent: z.number().int().min(1).max(100).default(80),
    action: budgetActionSchema.default("warn"),
    overrideRole: budgetOverrideRoleSchema.default("owner"),
    childDefault: budgetChildDefaultSchema.optional(),
});
/** `childDefault: null` takes the default share away. */
export const budgetPatchSchema = budgetCreateSchema.omit({ scope: true, childDefault: true }).partial().extend({ childDefault: budgetChildDefaultSchema.nullable().optional() });
export const budgetOverrideSchema = z.object({
    budgetId: z.string().min(1),
    reason: z.string().min(1).max(2000),
    hours: z.number().int().min(1).max(168).default(24),
});
/** `POST /admin/v1/budgets/:id/override`: administrators lift a budget for every project. */
export const budgetOrganisationOverrideSchema = budgetOverrideSchema.omit({ budgetId: true });
export const BUDGET_OVERRIDE_RPC = "aioe.budget.override";
/**
 * Relay order `budget.changed` (cost round 2): sent to every online device of the people a budget
 * covers when its state changes or it crosses a line, so a workbench re-reads
 * `GET /projects/v1/:id/budget` for the projects named now rather than at its next poll. `state` is the
 * budget's own state (before any project's lift); `percent` of its limit. Answer anything; nothing waits on it.
 */
export const BUDGET_CHANGED_ORDER = "budget.changed";
export const budgetChangedOrderSchema = z.object({
    budgetId: z.string(),
    scope: budgetScopeSchema,
    state: budgetStateSchema,
    percent: z.number(),
    /** The projects whose standing moved; empty when the budget covers projects with no usage yet. */
    projectIds: z.array(z.string()),
    name: z.string().optional(),
    /** What crossed: a threshold (50, 80, 100), "forecast", or "state". */
    reason: z.string().optional(),
});
/** What dispatch answers (409) when a budget holds new work, with the budget named in `message`. */
export const BUDGET_REFUSALS = { blocked: "budget_blocked", throttled: "budget_throttled" };
/** The organisation's budget settings on `PUT /admin/v1/organisation` (cost round 2): `{ budgets: { failClosed }, timeZone }`. */
export const organisationBudgetSettingsSchema = z.object({ failClosed: z.boolean() });
/** Plain words for a state. */
export const BUDGET_STATE_TEXT = { ok: "Within budget", warning: "Near the budget", throttled: "Paused for budget", blocked: "Blocked for budget" };
/** Whether a string is an IANA time zone this runtime knows ("UTC", "Australia/Sydney"). */
export function isTimeZone(tz) {
    if (!tz || tz.length > 64)
        return false;
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
export const timeZoneSchema = z.string().min(1).max(64).refine(isTimeZone, "an IANA time zone, such as Australia/Sydney");
/** How far a zone's wall clock is ahead of UTC at an instant, in milliseconds. */
export function zoneOffsetMs(timeZone, at) {
    if (timeZone === "UTC")
        return 0;
    const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(at).map((p) => [p.type, p.value]));
    const wall = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour) % 24, Number(parts.minute), Number(parts.second));
    return wall - Math.floor(at.getTime() / 1000) * 1000;
}
/** The instant a zone's wall clock reads midnight on a date (month and day may overflow, as with Date.UTC). */
export function zonedMidnight(timeZone, y, m, d) {
    const wall = Date.UTC(y, m, d);
    if (timeZone === "UTC")
        return wall;
    const first = wall - zoneOffsetMs(timeZone, new Date(wall));
    return wall - zoneOffsetMs(timeZone, new Date(first));
}
/** The date a zone's wall clock shows at an instant: year, month (0-11), day, weekday (0 Sunday). */
export function zonedDate(timeZone, at) {
    const local = new Date(at.getTime() + zoneOffsetMs(timeZone, at));
    return { y: local.getUTCFullYear(), m: local.getUTCMonth(), d: local.getUTCDate(), weekday: local.getUTCDay() };
}
/** The period a budget counts over, containing `now`, in the organisation's time zone (UTC when not given). */
export function budgetPeriod(period, now, timeZone = "UTC") {
    const { y, m, d, weekday } = zonedDate(timeZone, now);
    const iso = (t) => new Date(t).toISOString();
    if (period === "month")
        return { since: iso(zonedMidnight(timeZone, y, m, 1)), until: iso(zonedMidnight(timeZone, y, m + 1, 1)) };
    if (period === "week") {
        const back = (weekday + 6) % 7; // Monday first
        return { since: iso(zonedMidnight(timeZone, y, m, d - back)), until: iso(zonedMidnight(timeZone, y, m, d - back + 7)) };
    }
    return { since: iso(zonedMidnight(timeZone, y, m, d)), until: iso(zonedMidnight(timeZone, y, m, d + 1)) };
}
/**
 * Where a run-rate takes a budget by its period's end: `spent` so far this period, `trailing` the
 * usage of the `trailingMs` just before `now` (seven days), projected to `until`.
 */
export function budgetForecast(b, spent, trailing, trailingMs, now, until) {
    const left = Math.max(0, Date.parse(until) - now.getTime());
    const usdRate = trailing.usd / trailingMs;
    const tokenRate = trailing.tokens / trailingMs;
    const usd = Math.round((spent.usd + usdRate * left) * 100) / 100;
    const tokens = b.tokens ? Math.round(spent.tokens + tokenRate * left) : undefined;
    const percent = Math.round(Math.max((usd / b.amountUsd) * 100, b.tokens && tokens !== undefined ? (tokens / b.tokens) * 100 : 0) * 10) / 10;
    // When the limit is reached at this rate: the sooner of money and tokens.
    const whenMoney = usdRate > 0 && spent.usd < b.amountUsd ? (b.amountUsd - spent.usd) / usdRate : spent.usd >= b.amountUsd ? 0 : Infinity;
    const whenTokens = b.tokens ? (tokenRate > 0 && spent.tokens < b.tokens ? (b.tokens - spent.tokens) / tokenRate : spent.tokens >= b.tokens ? 0 : Infinity) : Infinity;
    const inMs = Math.min(whenMoney, whenTokens);
    const exceeds = inMs <= left;
    return { usd, ...(tokens !== undefined ? { tokens } : {}), percent, ...(exceeds ? { reachesLimitAt: new Date(now.getTime() + inMs).toISOString() } : {}), exceeds };
}
/** The state the spend gives, before any override. */
export function budgetStateFor(b, spent) {
    const byMoney = (spent.usd / b.amountUsd) * 100;
    const byTokens = b.tokens ? (spent.tokens / b.tokens) * 100 : 0;
    const percent = Math.round(Math.max(byMoney, byTokens) * 10) / 10;
    if (percent >= 100)
        return { state: b.action === "block" ? "blocked" : b.action === "throttle" ? "throttled" : "warning", percent };
    if (percent >= b.warnAtPercent)
        return { state: "warning", percent };
    return { state: "ok", percent };
}
const RANK = { ok: 0, warning: 1, throttled: 2, blocked: 3 };
export const worstBudgetState = (states) => states.reduce((a, b) => (RANK[b] > RANK[a] ? b : a), "ok");
// ── The hierarchy (cost round 3) ───────────────────────────────────────────
/** How long each period runs, in days, to compare a child with its parent. */
export const BUDGET_PERIOD_DAYS = { day: 1, week: 7, month: 31 };
/** Where a scope sits: the organisation above business areas above projects; teams and people beside. */
export const BUDGET_SCOPE_RANK = { tenant: 0, area: 1, project: 2, team: undefined, person: undefined };
/**
 * Whether a child budget asks for more than its parent allows: over a period as long as the
 * parent's or shorter, a limit (money, or tokens when both set them) above the parent's. A child
 * on a longer period than its parent is held by the parent's shorter period and never exceeds it.
 */
export function budgetExceeds(child, parent) {
    if (BUDGET_PERIOD_DAYS[child.period] > BUDGET_PERIOD_DAYS[parent.period])
        return false;
    return child.amountUsd > parent.amountUsd || (child.tokens !== undefined && parent.tokens !== undefined && child.tokens > parent.tokens);
}
/** The id an inherited share answers under: `<area budget id>~<project id>`. */
export const inheritedBudgetId = (areaBudgetId, projectId) => `${areaBudgetId}~${projectId}`;
/** The area budget an inherited share's id comes from, or undefined for a budget of its own. */
export const inheritedFrom = (budgetId) => {
    const i = budgetId.indexOf("~");
    return i > 0 ? { budgetId: budgetId.slice(0, i), projectId: budgetId.slice(i + 1) } : undefined;
};
/** A project's inherited share of an area budget: the area's limits scaled, its period, action, warning line and lifts. */
export function inheritedBudget(area, project, percent) {
    const { childDefault: _c, ...rest } = area;
    return {
        ...rest,
        id: inheritedBudgetId(area.id, project.id),
        name: `${project.name}: ${percent}% of ${area.name}`,
        scope: { type: "project", id: project.id },
        amountUsd: Math.max(0.01, Math.round(area.amountUsd * percent) / 100),
        ...(area.tokens ? { tokens: Math.max(1, Math.round((area.tokens * percent) / 100)) } : {}),
    };
}
//# sourceMappingURL=budgets.js.map